// Pure query → search-spec transform. The API layer takes the typed
// SearchSpec and feeds `normalized` into `computeBlindIndex` to build
// the bytea query; this module's job is the pre-crypto normalisation
// and total-function guarantees. No I/O, no throws.

import { normalizeDlForBidx } from "../customer-identity/dl-normalize.ts";
import { normalizeDobIsoDate } from "../customer-identity/dob-normalize.ts";
import { normalizeUsPhoneToE164 } from "../customer-identity/e164.ts";
import { normalizeEmail } from "../customer-identity/email-normalize.ts";
import { SEARCH_FIELDS, type SearchField, type SpecResult } from "./types.ts";

function isSearchField(value: string): value is SearchField {
  return (SEARCH_FIELDS as readonly string[]).includes(value);
}

// Lower-cased last/business name — the P1-17 PR H schema stores
// last_name_lower_bidx / business_name_lower_bidx keyed off the
// lower-cased form. Trim whitespace too; multi-space inside the name
// is preserved so "Van Buren" doesn't collide with "VanBuren".
function normalizeNameLower(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  return trimmed.toLowerCase();
}

export function queryToSearchSpec(input: {
  field: string;
  value: string;
  // Only consulted for `dl_number`; ignored otherwise. The stored
  // id_number_bidx is HMAC(key, "STATE:NUM"), so DL search needs
  // the state the DL was issued by.
  state?: string | null;
}): SpecResult {
  const { field, value, state } = input;

  // Field validation first — a bad `field` short-circuits before we
  // even look at the value.
  if (!isSearchField(field)) {
    return { ok: false, error: { kind: "unsupported_field", field } };
  }

  // Empty / whitespace-only values are rejected without naming the
  // field so the UI can give the same "enter a value" hint for every
  // input.
  if (typeof value !== "string" || value.trim().length === 0) {
    return { ok: false, error: { kind: "empty_value" } };
  }

  switch (field) {
    case "last_name": {
      const n = normalizeNameLower(value);
      if (n === null) return { ok: false, error: { kind: "empty_value" } };
      return { ok: true, spec: { field, normalized: n } };
    }
    case "business_name": {
      const n = normalizeNameLower(value);
      if (n === null) return { ok: false, error: { kind: "empty_value" } };
      return { ok: true, spec: { field, normalized: n } };
    }
    case "phone": {
      const e164 = normalizeUsPhoneToE164(value);
      if (e164 === null) return { ok: false, error: { kind: "unparseable", field } };
      return { ok: true, spec: { field, normalized: e164 } };
    }
    case "email": {
      const lower = normalizeEmail(value);
      if (lower === null) return { ok: false, error: { kind: "unparseable", field } };
      return { ok: true, spec: { field, normalized: lower } };
    }
    case "dl_number": {
      if (state === null || state === undefined || state.trim().length === 0) {
        return { ok: false, error: { kind: "missing_state", field } };
      }
      const dl = normalizeDlForBidx(state, value);
      if (dl === null) return { ok: false, error: { kind: "unparseable", field } };
      return { ok: true, spec: { field, normalized: dl.combined } };
    }
    case "dob": {
      const iso = normalizeDobIsoDate(value);
      if (iso === null) return { ok: false, error: { kind: "unparseable", field } };
      return { ok: true, spec: { field, normalized: iso } };
    }
  }
}

// Map a SearchField to the bidx column the API queries. Centralised so
// a schema rename shows up in one place.
export const BIDX_COLUMN: Readonly<Record<SearchField, { table: string; column: string }>> =
  Object.freeze({
    last_name: { table: "core.customers", column: "last_name_lower_bidx" },
    business_name: { table: "core.customers", column: "business_name_lower_bidx" },
    phone: { table: "core.customers", column: "mobile_phone_e164_bidx" },
    // `phone` targets the mobile column — the register's W1 flow asks
    // for the number they'll receive the receipt on, which is the
    // mobile. The work_phone column is reachable via a second, named
    // field if the owner ever wants it; keeping the surface minimal
    // today (data minimisation + fewer bidx round-trips).
    email: { table: "core.customers", column: "primary_email_lower_bidx" },
    dl_number: { table: "core.customer_identity", column: "id_number_bidx" },
    dob: { table: "core.customer_identity", column: "dob_bidx" },
  });
